import { Button } from '@territory/ui';
import { BRAND } from './wedgeBrand';
import { CSSProperties, useEffect, useMemo, useState } from 'react';

/**
 * /copy — bridge route for the cold-email "Tap to copy" link.
 *
 * Email clients strip JS, so the access-code email cannot run
 * navigator.clipboard.writeText() directly. The amber code block in the email
 * is wrapped in <a href="/copy?code=X&email=Y">; this route does the actual
 * clipboard write, shows a confirmation, and then redirects to /?code=X&email=Y
 * so CodeRedeem pre-fills both fields.
 *
 * Mounted outside WedgeAuthGate (in main.tsx) so cold links from email don't
 * bounce off the auth wall before redeeming.
 */

type Status = 'pending' | 'copied' | 'manual';

function legacyCopy(value: string): boolean {
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function CopyCode() {
  const { code, email } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      code: (params.get('code') || '').trim(),
      email: (params.get('email') || '').trim(),
    };
  }, []);

  const prepUrl = useMemo(() => {
    const qs: string[] = [];
    if (code) qs.push(`code=${encodeURIComponent(code)}`);
    if (email) qs.push(`email=${encodeURIComponent(email)}`);
    return qs.length ? `/?${qs.join('&')}` : '/';
  }, [code, email]);

  const [status, setStatus] = useState<Status>('pending');

  useEffect(() => {
    if (!code) {
      setStatus('manual');
      return;
    }

    let cancelled = false;

    const onSuccess = () => {
      if (cancelled) return;
      setStatus('copied');
      window.setTimeout(() => {
        if (!cancelled) window.location.replace(prepUrl);
      }, 1400);
    };

    const onFail = () => {
      if (!cancelled) setStatus('manual');
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(code)
        .then(onSuccess)
        .catch(() => {
          if (legacyCopy(code)) onSuccess();
          else onFail();
        });
    } else if (legacyCopy(code)) {
      onSuccess();
    } else {
      onFail();
    }

    return () => {
      cancelled = true;
    };
  }, [code, prepUrl]);

  const titleText =
    status === 'copied'
      ? 'Code copied.'
      : status === 'manual'
        ? 'Tap your code below.'
        : 'Copying your code…';

  const subText =
    status === 'copied'
      ? 'Opening PREP…'
      : status === 'manual'
        ? 'Long-press to copy, then open PREP.'
        : 'Opening PREP in a moment.';

  const wrap: CSSProperties = {
    minHeight: '100vh',
    background:
      BRAND.screenBg,
    color: BRAND.text,
    fontFamily: BRAND.fontBody,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box',
  };

  const card: CSSProperties = {
    width: '100%',
    maxWidth: 440,
    background: 'rgba(52,211,153,0.04)',
    border: '1px solid rgba(52,211,153,0.15)',
    borderRadius: 16,
    padding: '44px 32px 36px',
    textAlign: 'center',
    boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
  };

  const wordmark: CSSProperties = {
    fontFamily: BRAND.fontDisplay,
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: 4,
    textTransform: 'uppercase',
    marginBottom: 8,
    color: '#F0F0F0',
    lineHeight: 1,
  };

  const tagline: CSSProperties = {
    fontSize: 10,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    fontFamily: "'Share Tech Mono', ui-monospace, Menlo, monospace",
    color: BRAND.textMuted,
    marginBottom: 36,
  };

  const checkWrap: CSSProperties = {
    display: 'inline-block',
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: BRAND.accent,
    position: 'relative',
    marginBottom: 18,
    opacity: status === 'copied' ? 1 : 0,
    transform: status === 'copied' ? 'scale(1)' : 'scale(0.6)',
    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
  };

  const checkMark: CSSProperties = {
    position: 'absolute',
    top: 14,
    left: 19,
    width: 9,
    height: 18,
    border: 'solid #fff',
    borderWidth: '0 3px 3px 0',
    transform: 'rotate(45deg)',
  };

  const statusStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.2,
    lineHeight: 1.3,
    margin: '0 0 6px',
    color: BRAND.text,
  };

  const codePill: CSSProperties = {
    fontFamily:
      "'SF Mono', ui-monospace, 'Cascadia Mono', Menlo, Consolas, monospace",
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: 4,
    color: BRAND.amber,
    background: 'rgba(245,158,11,0.10)',
    border: '1px solid rgba(245,158,11,0.45)',
    borderRadius: 8,
    padding: '12px 18px',
    display: 'inline-block',
    margin: '14px 0 18px',
    userSelect: 'all',
  };

  const subStyle: CSSProperties = {
    fontSize: 13,
    lineHeight: 1.55,
    color: BRAND.textSec,
    margin: '0 0 22px',
  };

  const manualNote: CSSProperties = {
    display: status === 'manual' ? 'block' : 'none',
    fontSize: 11.5,
    color: BRAND.textMuted,
    marginTop: 16,
    lineHeight: 1.5,
  };

  const footer: CSSProperties = {
    marginTop: 22,
    paddingTop: 18,
    borderTop: '1px solid rgba(52,211,153,0.12)',
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    fontFamily: "'Share Tech Mono', ui-monospace, Menlo, monospace",
    color: BRAND.textDim,
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={wordmark}>
          <span style={{ color: '#F0F0F0' }}>REP</span><span style={{ color: BRAND.amber }}>VANTAGE</span>
        </div>
        <div style={tagline}>Sales Intelligence Platform</div>

        <div style={checkWrap} aria-hidden="true">
          <div style={checkMark} />
        </div>
        <p style={statusStyle}>{titleText}</p>
        <div style={codePill}>{code || '(no code in link)'}</div>
        <p style={subStyle}>{subText}</p>

        <Button appearance="wedge-redirect" redirectLook="copy-code" href={prepUrl}>
          Open PREP now →
        </Button>

        <div style={manualNote}>
          Could not copy automatically. Long-press the code above to copy by
          hand, then tap Open PREP.
        </div>

        <div style={footer}>© 2026 RepVantage LLC · Made for the field</div>
      </div>
    </div>
  );
}
